/**
 * Store — the buyer side of the marketplace (company admins).
 *
 *   Shop       published hardware grouped by provider; click a product for
 *              its full detail (photos, description, specs, documents), add
 *              to cart, then check out — the cart splits into one order per
 *              hardware provider and each is charged to the card on file
 *              (an order that can't charge is recorded pending, with the
 *              reason)
 *   My orders  purchase history: lines, shipment tracking, and the devices
 *              the seller registered to this organization
 *
 * Tab selection is URL-driven (`?tab=`); the cart persists per browser.
 */

import { Fragment, useEffect, useState } from 'react';
import { useSearchParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { IconX } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { isCompanyAdmin } from '../services/users';
import {
  getStoreCatalog, getStoreOrders, getStoreOrder, getStoreProduct,
  loadCart, saveCart, retryOrderPayment, cancelOrder, paymentReasonCopy,
  StoreVariant, StoreBoard, StoreProvider, CartItem,
} from '../services/store';
import { formatMoney } from '../services/deviceBoards';
import { assetPreviewUrl, publicFileUrl } from '../services/listings';
import { formatWhen } from '../components/BoardPriceModal';
import { AuthImage } from '../components/AuthImage';
import { SegmentedTabs } from '../components/SegmentedTabs';
import {
  PageContainer, PageHeader, PageTitle, SectionTitle,
  TableContainer, StyledTable, Th, Td, StatusChip,
  LoadingText, EmptyState, MutedText, Caption, Row,
  Button, LinkButton, Input, FormGroup, Label, HelperText, ErrorAlert,
  ModalOverlay, ModalCard, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, H2Title,
  DetailCard, CollapsibleHeader, Chevron, CardBody,
} from '../components/styled';

type StoreTab = 'shop' | 'orders';

const ProviderBlock = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(8)};
`;

const BoardHeading = styled.div`
  margin: ${({ theme }) => theme.spacing(4)} 0 ${({ theme }) => theme.spacing(2)};
`;

const Thumb = styled.div`
  width: 56px;
  height: 42px;
  border-radius: ${({ theme }) => theme.border.radius.md};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  background: ${({ theme }) => theme.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img { max-width: 100%; max-height: 100%; object-fit: contain; }
`;

const ProductCell = styled(Td)`
  cursor: pointer;

  &:hover strong { text-decoration: underline; }
`;

const OrderRow = styled.tr`
  cursor: pointer;
  &:hover td { background: ${({ theme }) => theme.background.secondary}; }
`;

const ExpandTd = styled(Td)`
  background: ${({ theme }) => theme.background.secondary};
`;

const DetailColumns = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(8)};
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

const GalleryRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  img {
    max-height: 140px;
    max-width: 200px;
    object-fit: contain;
    border: 1px solid ${({ theme }) => theme.border.color.light};
    border-radius: ${({ theme }) => theme.border.radius.md};
    background: ${({ theme }) => theme.background.secondary};
  }
`;

const SpecTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.font.size.sm};

  td {
    padding: ${({ theme }) => theme.spacing(1, 2)};
    border-bottom: 1px solid ${({ theme }) => theme.border.color.light};
  }
  td:first-of-type { color: ${({ theme }) => theme.font.color.tertiary}; white-space: nowrap; }
`;

type OrderChipVariant = 'warning' | 'blue' | 'positive' | 'green' | 'neutral' | 'critical';

const orderVariant = (status: string): OrderChipVariant =>
  status === 'pending' ? 'warning'
    : status === 'paid' ? 'blue'
    : status === 'shipped' ? 'positive'
    : status === 'completed' ? 'green' : 'neutral';

/** A pending card order with a failed charge needs the buyer's action —
 * distinct from a pending terms order, which just awaits the seller. */
function orderStatusDisplay(o: { status: string; payment_terms?: string; payment_failure_reason?: string | null }): { label: string; variant: OrderChipVariant } {
  if (o.status === 'pending' && o.payment_terms === 'card' && o.payment_failure_reason) {
    return { label: 'payment needed', variant: 'critical' };
  }
  return { label: o.status, variant: orderVariant(o.status) };
}

// ── Product detail modal ─────────────────────────────────────────────────────

function ProductDetailModal({ variantId, onAdd, onClose }: {
  variantId: number;
  onAdd: (item: Omit<CartItem, 'quantity'>, qty: number) => void;
  onClose: () => void;
}) {
  const [qty, setQty] = useState('1');
  const { data: p, isLoading } = useQuery({
    queryKey: ['store-product', variantId],
    queryFn: () => getStoreProduct(variantId),
  });

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard size="md" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <H2Title>{p ? p.sku : 'Product'}</H2Title>
          <ModalCloseButton onClick={onClose} aria-label="Close"><IconX size={18} stroke={2} /></ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          {isLoading || !p ? <LoadingText>Loading product…</LoadingText> : (
            <>
              {p.images.length > 0 && (
                <GalleryRow>
                  {p.images.slice(0, 4).map((img) => (
                    <img key={img.id} src={publicFileUrl(img.id)} alt={img.title || p.name} />
                  ))}
                </GalleryRow>
              )}
              <div>
                <strong>{p.name}</strong>
                <MutedText> — {p.board_display_name || p.board} by {p.provider_name}</MutedText>
                {' '}<StatusChip variant={p.status === 'available' ? 'positive' : 'neutral'}>{p.status}</StatusChip>
              </div>
              {p.headline && <MutedText>{p.headline}</MutedText>}
              <div style={{ margin: '8px 0' }}>
                {p.current_price ? (
                  <>
                    <strong>{formatMoney(p.current_price.hardware_price_cents, p.current_price.currency)}</strong> per unit
                    {p.current_price.monthly_fee_cents > 0 && (
                      <MutedText> + {formatMoney(p.current_price.monthly_fee_cents, p.current_price.currency)}/mo per deployed device</MutedText>
                    )}
                  </>
                ) : <MutedText>Not priced yet</MutedText>}
              </div>
              {(p.description || p.board_description) && (
                <p style={{ whiteSpace: 'pre-wrap' }}>{p.description || p.board_description}</p>
              )}
              {p.specs.length > 0 && (
                <SpecTable>
                  <tbody>
                    {p.specs.map((s, i) => (
                      <tr key={i}>
                        <td>{s.group ? `${s.group} · ${s.label}` : s.label}</td>
                        <td>{s.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </SpecTable>
              )}
              {p.documents.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Caption>Documents</Caption>
                  {p.documents.map((d) => (
                    <div key={d.id}>
                      <a href={publicFileUrl(d.id)} target="_blank" rel="noreferrer">{d.title || d.file_name}</a>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Input type="number" min={1} max={500} value={qty} onChange={(e) => setQty(e.target.value)} style={{ maxWidth: 90 }} />
          <Button
            type="button"
            disabled={!p || p.status !== 'available' || !p.current_price}
            onClick={() => {
              if (!p || !p.current_price) return;
              onAdd({
                variantId: p.id, boardId: p.board_id, sku: p.sku, name: p.name,
                providerName: p.provider_name, manufacturerId: p.manufacturer_id,
                providerOffersTerms: p.manufacturer_offers_terms === true,
                unitPriceCents: p.current_price.hardware_price_cents,
                monthlyFeeCents: p.current_price.monthly_fee_cents,
                currency: p.current_price.currency,
                heroAssetId: p.hero_asset_id,
              }, Math.max(1, parseInt(qty, 10) || 1));
              onClose();
            }}
          >
            Add to cart
          </Button>
        </ModalFooter>
      </ModalCard>
    </ModalOverlay>
  );
}

// ── Cart modal ───────────────────────────────────────────────────────────────

// ── Shop tab ─────────────────────────────────────────────────────────────────

function ShopPanel({ onOpenProduct, onAdd }: {
  onOpenProduct: (variantId: number) => void;
  onAdd: (item: Omit<CartItem, 'quantity'>, qty: number) => void;
}) {
  const { data, isLoading } = useQuery({ queryKey: ['store-catalog'], queryFn: getStoreCatalog });
  const providers = data?.providers ?? [];

  // Everything starts collapsed (Structure.md) — the vendor list is the index.
  const [openProviders, setOpenProviders] = useState<Set<number>>(new Set());
  const toggleProvider = (id: number) => {
    setOpenProviders((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (isLoading) return <LoadingText>Loading catalog...</LoadingText>;
  if (providers.length === 0) {
    return <EmptyState><p>No hardware is published for sale yet.</p></EmptyState>;
  }

  const addFrom = (p: StoreProvider, b: StoreBoard, v: StoreVariant) => {
    if (!v.current_price) return;
    onAdd({
      variantId: v.id, boardId: v.board_id, sku: v.sku, name: v.name,
      providerName: p.name, manufacturerId: p.manufacturer_id,
      providerOffersTerms: p.offers_terms === true,
      unitPriceCents: v.current_price.hardware_price_cents,
      monthlyFeeCents: v.current_price.monthly_fee_cents,
      currency: v.current_price.currency,
      heroAssetId: v.hero_asset_id,
    }, 1);
  };

  return (
    <>
      {providers.map((p) => (
        <ProviderBlock key={p.manufacturer_id}>
          <DetailCard>
            <CollapsibleHeader onClick={() => toggleProvider(p.manufacturer_id)}>
              <div>
                <SectionTitle style={{ margin: 0 }}>{p.name}</SectionTitle>
                {p.tagline && <MutedText>{p.tagline}</MutedText>}
              </div>
              <Row gap={2} align="center">
                <Caption>{p.boards.reduce((n, b) => n + b.variants.length, 0)} products</Caption>
                <Chevron open={openProviders.has(p.manufacturer_id)} />
              </Row>
            </CollapsibleHeader>
            {openProviders.has(p.manufacturer_id) && (
              <CardBody>
                {p.boards.map((b) => (
            <div key={b.board_id}>
              <BoardHeading>
                <strong>{b.display_name || b.board}</strong>
                {b.headline && <Caption>&nbsp;— {b.headline}</Caption>}
              </BoardHeading>
              <TableContainer>
                <StyledTable>
                  <thead>
                    <tr>
                      <Th></Th>
                      <Th>SKU</Th>
                      <Th>Product</Th>
                      <Th>Price</Th>
                      <Th>Monthly / device</Th>
                      <Th>Status</Th>
                      <Th></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.variants.map((v) => (
                      <tr key={v.id}>
                        <ProductCell onClick={() => onOpenProduct(v.id)}>
                          <Thumb>
                            {v.hero_asset_id
                              ? <AuthImage src={assetPreviewUrl(v.board_id, v.hero_asset_id)} alt={v.name} fallback={<Caption>—</Caption>} />
                              : <Caption>—</Caption>}
                          </Thumb>
                        </ProductCell>
                        <ProductCell onClick={() => onOpenProduct(v.id)}><strong>{v.sku}</strong></ProductCell>
                        <ProductCell onClick={() => onOpenProduct(v.id)}>
                          {v.name}
                          {v.headline && <div><Caption>{v.headline}</Caption></div>}
                        </ProductCell>
                        <Td>{v.current_price ? formatMoney(v.current_price.hardware_price_cents, v.current_price.currency) : <MutedText>—</MutedText>}</Td>
                        <Td>{v.current_price && v.current_price.monthly_fee_cents > 0
                          ? formatMoney(v.current_price.monthly_fee_cents, v.current_price.currency)
                          : <MutedText>none</MutedText>}</Td>
                        <Td>
                          <StatusChip variant={v.status === 'available' ? 'positive' : 'neutral'}>{v.status}</StatusChip>
                        </Td>
                        <Td>
                          <Button
                            size="sm"
                            disabled={v.status !== 'available' || !v.current_price}
                            onClick={() => addFrom(p, b, v)}
                          >
                            Add to cart
                          </Button>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </TableContainer>
            </div>
                ))}
              </CardBody>
            )}
          </DetailCard>
        </ProviderBlock>
      ))}
    </>
  );
}

// ── Orders tab ───────────────────────────────────────────────────────────────

export function BuyerOrderDetail({ orderId }: { orderId: number }) {
  const queryClient = useQueryClient();
  const { data: order, isLoading } = useQuery({
    queryKey: ['store-order', orderId],
    queryFn: () => getStoreOrder(orderId),
  });
  const [retryError, setRetryError] = useState('');
  const [cancelError, setCancelError] = useState('');

  const invalidateOrder = () => {
    queryClient.invalidateQueries({ queryKey: ['store-order', orderId] });
    queryClient.invalidateQueries({ queryKey: ['store-orders'] });
  };

  const retryMutation = useMutation({
    mutationFn: () => retryOrderPayment(orderId),
    onSuccess: () => setRetryError(''),
    onError: (err: any) => setRetryError(err.response?.data?.error || 'Could not retry the charge'),
    onSettled: invalidateOrder,
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelOrder(orderId),
    onSuccess: () => setCancelError(''),
    onError: (err: any) => setCancelError(err.response?.data?.error || 'Could not cancel this order'),
    onSettled: invalidateOrder,
  });

  if (isLoading || !order) return <LoadingText>Loading order…</LoadingText>;

  const isPending = order.status === 'pending';
  const canRetryCharge = isPending && order.payment_terms === 'card';
  const needsCardOnFile = order.payment_failure_reason === 'no_payment_method'
    || order.payment_failure_reason === 'payments_not_configured';

  const confirmCancel = () => {
    if (window.confirm(`Cancel order ${order.order_number}? This can't be undone.`)) {
      cancelMutation.mutate();
    }
  };

  return (
    <DetailColumns>
      {isPending && (
        <div>
          <Caption>Payment</Caption>
          <MutedText>
            {canRetryCharge ? paymentReasonCopy(order.payment_failure_reason ?? null) : paymentReasonCopy('terms')}
          </MutedText>
          {retryError && <ErrorAlert>{retryError}</ErrorAlert>}
          {cancelError && <ErrorAlert>{cancelError}</ErrorAlert>}
          <Row gap={2} style={{ marginTop: 6 }}>
            {canRetryCharge && (
              needsCardOnFile
                ? <Link to="/purchases?tab=payment">Add a payment method</Link>
                : (
                  <Button onClick={() => retryMutation.mutate()} disabled={retryMutation.isPending}>
                    {retryMutation.isPending ? 'Retrying…' : 'Retry payment'}
                  </Button>
                )
            )}
            <Button variant="danger" onClick={confirmCancel} disabled={cancelMutation.isPending}>
              {cancelMutation.isPending ? 'Cancelling…' : 'Cancel order'}
            </Button>
          </Row>
        </div>
      )}
      <div>
        <Caption>Items</Caption>
        {order.items.map((it) => (
          <div key={it.id}>
            {it.quantity} × {it.description || it.board}
            <MutedText> · {formatMoney(it.unit_price_cents, order.currency)} each</MutedText>
            {it.monthly_fee_cents > 0 && <MutedText> · {formatMoney(it.monthly_fee_cents, order.currency)}/mo per device</MutedText>}
          </div>
        ))}
      </div>
      {(order.carrier || order.tracking_number) && (
        <div>
          <Caption>Shipment</Caption>
          <div>{order.carrier || '—'}</div>
          {order.tracking_number && <MutedText>{order.tracking_number}</MutedText>}
          {order.shipped_at && <div><MutedText>shipped {formatWhen(order.shipped_at)}</MutedText></div>}
        </div>
      )}
      <div>
        <Caption>Devices ({order.registered_count})</Caption>
        {order.registered_devices.length === 0
          ? <MutedText>Registered by the seller at shipment; they appear on your Devices page.</MutedText>
          : order.registered_devices.map((d) => (
            <div key={d.device_id}>
              <Link to={`/devices/${d.device_id}`}>{d.device_id}</Link>
              <MutedText> · {d.status}{d.deployed ? ' · deployed' : ''}</MutedText>
            </div>
          ))}
      </div>
    </DetailColumns>
  );
}

export function OrdersPanel() {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['store-orders'], queryFn: getStoreOrders });
  const orders = data?.orders ?? [];

  if (isLoading) return <LoadingText>Loading orders...</LoadingText>;
  if (orders.length === 0) {
    return <EmptyState><p>No purchases yet.</p><MutedText>Hardware you buy in the Shop shows up here.</MutedText></EmptyState>;
  }

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            <Th>Order</Th>
            <Th>Provider</Th>
            <Th>Placed</Th>
            <Th>Units</Th>
            <Th>Devices</Th>
            <Th>Total</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o: any) => {
            const status = orderStatusDisplay(o);
            return (
            <Fragment key={o.id}>
              <OrderRow onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}>
                <Td><strong>{o.order_number}</strong></Td>
                <Td>{o.manufacturer_name}</Td>
                <Td><Caption>{formatWhen(o.created_at)}</Caption></Td>
                <Td>{o.units}</Td>
                <Td>
                  {(o.registered_count ?? 0) >= (o.units ?? 0)
                    ? <StatusChip variant="positive">{o.registered_count}/{o.units}</StatusChip>
                    : <MutedText>{o.registered_count ?? 0}/{o.units}</MutedText>}
                </Td>
                <Td>{formatMoney(o.subtotal_cents, o.currency)}</Td>
                <Td><StatusChip variant={status.variant}>{status.label}</StatusChip></Td>
              </OrderRow>
              {expandedId === o.id && (
                <tr>
                  <ExpandTd colSpan={7}>
                    <BuyerOrderDetail orderId={o.id} />
                  </ExpandTd>
                </tr>
              )}
            </Fragment>
            );
          })}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function Store() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const [cart, setCartState] = useState<CartItem[]>(() => loadCart());
  const [detailVariantId, setDetailVariantId] = useState<number | null>(null);

  const setCart = (items: CartItem[]) => {
    setCartState(items);
    saveCart(items);
  };

  useEffect(() => { saveCart(cart); }, [cart]);

  const addToCart = (item: Omit<CartItem, 'quantity'>, qty: number) => {
    const existing = cart.find((it) => it.variantId === item.variantId);
    if (existing) {
      setCart(cart.map((it) => (it.variantId === item.variantId ? { ...it, quantity: it.quantity + qty } : it)));
    } else {
      setCart([...cart, { ...item, quantity: qty }]);
    }
  };

  // Old Store links (?tab=orders) now live on the Purchases page.
  if (searchParams.get('tab') === 'orders') {
    return <Navigate to="/purchases" replace />;
  }

  if (!isCompanyAdmin(user)) {
    return (
      <PageContainer>
        <PageHeader><PageTitle>Purchase Material</PageTitle></PageHeader>
        <EmptyState><p>Hardware purchasing is available to company admins.</p></EmptyState>
      </PageContainer>
    );
  }

  const cartCount = cart.reduce((s, it) => s + it.quantity, 0);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Purchase Material</PageTitle>
        <Button variant={cartCount > 0 ? 'primary' : 'secondary'} onClick={() => navigate('/cart')}>
          Cart{cartCount > 0 ? ` (${cartCount})` : ''}
        </Button>
      </PageHeader>
      <ShopPanel onOpenProduct={setDetailVariantId} onAdd={addToCart} />

      {detailVariantId != null && (
        <ProductDetailModal
          variantId={detailVariantId}
          onAdd={addToCart}
          onClose={() => setDetailVariantId(null)}
        />
      )}

    </PageContainer>
  );
}

export default Store;
